import { useEffect, useMemo, useState } from "react";
import { api, gbp, formatApiError, fileUrl } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Download, Loader2 } from "lucide-react";
import { toast } from "sonner";

// Default period = current week (Mon → Sun).
function weekBounds(d = new Date()) {
  const day = d.getDay();                   // 0 = Sun, 1 = Mon...
  const mondayOffset = day === 0 ? -6 : 1 - day;
  const monday = new Date(d);
  monday.setDate(d.getDate() + mondayOffset);
  const sunday = new Date(monday);
  sunday.setDate(monday.getDate() + 6);
  const iso = (x) => x.toISOString().slice(0, 10);
  return { from: iso(monday), to: iso(sunday) };
}

export default function FinanceProfit() {
  const [{ from, to }, setRange] = useState(weekBounds());
  const [clients, setClients] = useState([]);
  const [clientId, setClientId] = useState("__all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get("/clients").then((r) => {
      const list = [...(r.data || [])].sort((a, b) => (a.name || "").localeCompare(b.name || ""));
      setClients(list);
    }).catch(() => setClients([]));
  }, []);

  const load = async () => {
    if (!from || !to || to < from) { toast.error("Pick a valid date range"); return; }
    setLoading(true);
    try {
      const params = { date_from: from, date_to: to };
      if (clientId && clientId !== "__all") params.client_id = clientId;
      const r = await api.get("/finance/profit-weekly", { params });
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const pdfHref = useMemo(() => {
    const qs = new URLSearchParams({ date_from: from, date_to: to });
    if (clientId && clientId !== "__all") qs.set("client_id", clientId);
    return fileUrl(`/finance/profit-weekly.pdf?${qs.toString()}`);
  }, [from, to, clientId]);

  const setWeek = (delta) => {
    const monday = new Date(from);
    monday.setDate(monday.getDate() + delta * 7);
    const b = weekBounds(monday);
    setRange(b);
  };

  const groups = data?.clients || [];
  const totals = data?.totals || {};

  return (
    <div data-testid="finance-profit-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.22em] text-orange-700 font-semibold">Finance</div>
          <h1 className="font-display text-3xl tracking-tight font-semibold text-slate-900 mt-2">Profit</h1>
          <p className="text-sm text-slate-600 mt-1">Actual charge − actual driver pay from every job in the selected period. Download a PDF or use the on-screen table.</p>
        </div>
      </div>

      <Card className="rounded-sm border border-slate-200 shadow-none p-4">
        <div className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div>
            <Label className="text-xs text-slate-600">From</Label>
            <Input data-testid="profit-from" type="date" value={from} onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} className="mt-1 rounded-sm border-slate-300" />
          </div>
          <div>
            <Label className="text-xs text-slate-600">To</Label>
            <Input data-testid="profit-to" type="date" value={to} onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} className="mt-1 rounded-sm border-slate-300" />
          </div>
          <div className="md:col-span-2">
            <Label className="text-xs text-slate-600">Client</Label>
            <Select value={clientId} onValueChange={setClientId}>
              <SelectTrigger data-testid="profit-client" className="mt-1 rounded-sm border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__all">All clients</SelectItem>
                {clients.map((c) => (<SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>))}
              </SelectContent>
            </Select>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => setWeek(-1)} data-testid="profit-prev-week">← Prev week</Button>
            <Button variant="outline" size="sm" className="rounded-sm h-9" onClick={() => setWeek(1)} data-testid="profit-next-week">Next week →</Button>
          </div>
          <div className="flex gap-2">
            <Button className="rounded-sm bg-slate-900 hover:bg-slate-800 h-9" onClick={load} disabled={loading} data-testid="profit-run">
              {loading ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
              Run
            </Button>
            <a
              data-testid="profit-download"
              href={pdfHref}
              download
              rel="noopener"
              className="inline-flex items-center gap-1.5 text-xs font-medium px-3 py-2 rounded-sm border border-emerald-300 text-emerald-800 bg-emerald-50 hover:bg-emerald-100"
            >
              <Download className="w-3.5 h-3.5" /> PDF
            </a>
          </div>
        </div>
      </Card>

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="profit-summary">
        <StatCard label="Jobs" value={totals.jobs ?? 0} />
        <StatCard label="Total charge" value={gbp(totals.charge || 0)} />
        <StatCard label="Total pay" value={gbp(totals.pay || 0)} />
        <StatCard
          label="Total profit"
          value={`${gbp(totals.profit || 0)}  ·  ${(totals.margin_pct || 0).toFixed(0)}%`}
          tone={(totals.profit || 0) >= 0 ? "good" : "bad"}
        />
      </div>

      {groups.length === 0 && !loading && (
        <Card className="rounded-sm border border-dashed border-slate-300 shadow-none p-10 text-center text-sm text-slate-600" data-testid="profit-empty">
          No jobs in this date range. Try widening the dates or picking a different client.
        </Card>
      )}

      {groups.map((g) => (
        <Card key={g.client_id} className="rounded-sm border border-slate-200 shadow-none" data-testid={`profit-client-${g.client_id}`}>
          <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-4">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500">Client</div>
              <div className="text-sm font-semibold text-slate-900">{g.client_name}</div>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-slate-600 tabular-nums">
              <span>Jobs <span className="font-semibold text-slate-900">{g.jobs}</span></span>
              <span>Charge <span className="font-semibold text-slate-900">{gbp(g.charge)}</span></span>
              <span>Pay <span className="font-semibold text-slate-900">{gbp(g.pay)}</span></span>
              <span className={`font-semibold ${g.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                Profit {gbp(g.profit)} ({(g.margin_pct || 0).toFixed(0)}%)
              </span>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 uppercase text-[11px] tracking-wider">
                <tr>
                  <th className="text-left px-5 py-2.5">Date</th>
                  <th className="text-left px-5 py-2.5">Job #</th>
                  <th className="text-left px-5 py-2.5">Service</th>
                  <th className="text-right px-5 py-2.5">Charge</th>
                  <th className="text-right px-5 py-2.5">Pay</th>
                  <th className="text-right px-5 py-2.5">Profit</th>
                </tr>
              </thead>
              <tbody>
                {g.rows.map((r, i) => (
                  <tr key={i} className="border-t border-slate-100">
                    <td className="px-5 py-2.5 tabular-nums text-slate-700">{r.date}</td>
                    <td className="px-5 py-2.5 font-mono text-xs text-slate-700">{r.number}</td>
                    <td className="px-5 py-2.5 text-slate-700">
                      {r.service_type}
                      {r.estimated && (
                        <span className="ml-2 inline-block text-[9.5px] uppercase tracking-wider px-1.5 py-0.5 rounded-sm bg-amber-100 text-amber-800 border border-amber-200" title="Charge and/or pay were 0 on the job — the report used the rate card as a fallback so you can still see the expected profit.">
                          est. from rate card
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{gbp(r.charge)}</td>
                    <td className="px-5 py-2.5 text-right tabular-nums">{gbp(r.pay)}</td>
                    <td className={`px-5 py-2.5 text-right tabular-nums font-semibold ${r.profit >= 0 ? "text-emerald-700" : "text-red-600"}`}>
                      {gbp(r.profit)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      ))}

      <p className="text-[11px] text-slate-500">Charge and pay figures come from each Job record. When a job has £0 saved, the report falls back to the Chargeable / Payable rate card for that client-site (marked <span className="font-semibold text-amber-700">est. from rate card</span>).</p>
    </div>
  );
}

function StatCard({ label, value, tone }) {
  const valColor =
    tone === "good" ? "text-emerald-700" :
    tone === "bad"  ? "text-red-600"   : "text-slate-900";
  return (
    <Card className="rounded-sm border border-slate-200 shadow-none p-4">
      <div className="text-[11px] uppercase tracking-wider text-slate-500">{label}</div>
      <div className={`text-xl font-semibold mt-1 tabular-nums ${valColor}`}>{value}</div>
    </Card>
  );
}
